import React, { useState, useEffect } from 'react';
import { ref, push, onValue, update, remove } from 'firebase/database';
import { db, storage } from '../firebase'; // Make sure storage is exported from your firebase config
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const AddInternationalTour = () => {
  const [form, setForm] = useState({
    title: '',
    nights: '', // Changed from duration to nights
    days: '',   // Added days field
    description: '',
    image: ''
  });
  const [imageFile, setImageFile] = useState(null); // For file upload
  const [uploadProgress, setUploadProgress] = useState(0); // Track upload progress
  const [isUploading, setIsUploading] = useState(false); // Loading state
  const [tours, setTours] = useState([]);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const tourRef = ref(db, 'tours');
    onValue(tourRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const filtered = Object.entries(data)
          .map(([id, value]) => ({ id, ...value }))
          .filter(item => item.category === 'international-tour');
        setTours(filtered);
      }
    });
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
      // Clear the image URL if a new file is selected
      setForm(prev => ({
        ...prev,
        image: ''
      }));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const fileRef = storageRef(storage, `tour-images/${Date.now()}-${imageFile.name}`);
      const uploadTask = uploadBytes(fileRef, imageFile);
      
      // If you want to track upload progress (optional)
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setIsUploading(false);
        }
      );
      
      await uploadTask;
      const downloadURL = await getDownloadURL(fileRef);
      setIsUploading(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      setIsUploading(false);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, nights, days, description, image } = form;
    
    // Validation with proper error messages
    if (!title || !nights || !days || !description || (!image && !imageFile)) {
      alert('Please fill in all fields including nights, days, and select an image.');
      return;
    }

    // Validate that nights and days are numbers
    const nightsNum = parseInt(nights, 10);
    const daysNum = parseInt(days, 10);
    
    if (isNaN(nightsNum) || isNaN(daysNum) || nightsNum < 0 || daysNum < 1) {
      alert('Please enter valid numbers for nights and days. Days must be at least 1.');
      return;
    }

    // Logical validation: days should typically be nights + 1
    if (daysNum !== nightsNum + 1) {
      const confirm = window.confirm(
        `Typically, ${nightsNum} nights should be ${nightsNum + 1} days. You entered ${daysNum} days. Do you want to continue?`
      );
      if (!confirm) return;
    }

    let imageUrl = image;
    if (imageFile) {
      imageUrl = await uploadImage();
      if (!imageUrl) {
        alert('Failed to upload image. Please try again.');
        return;
      }
    }

    const newTour = {
      title,
      nights: nightsNum,
      days: daysNum,
      description,
      image: imageUrl,
      category: 'international-tour',
      createdAt: new Date().toISOString()
    };

    if (editId) {
      update(ref(db, `tours/${editId}`), newTour)
        .then(() => {
          alert('Tour updated!');
          resetForm();
        });
    } else {
      push(ref(db, 'tours'), newTour)
        .then(() => {
          alert('International Tour added!');
          resetForm();
        });
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      nights: '',
      days: '',
      description: '',
      image: ''
    });
    setImageFile(null);
    setEditId(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleEdit = (tour) => {
    setForm({
      title: tour.title,
      nights: tour.nights || '',
      days: tour.days || '',
      description: tour.description,
      image: tour.image
    });
    setEditId(tour.id);
    setImageFile(null); // Clear any selected file when editing
    window.scrollTo(0, 0);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this tour?')) {
      remove(ref(db, `tours/${id}`))
        .then(() => {
          alert('Tour deleted.');
        });
    }
  };

  return (
    <div style={styles.container}>
      <h2>{editId ? 'Edit' : 'Add'} International Tour</h2>
      
      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          name="title"
          placeholder="Tour Title"
          value={form.title}
          onChange={handleChange}
          style={styles.input}
          required
        />
        
        {/* Nights and Days inputs side by side */}
        <div style={styles.durationContainer}>
          <div style={styles.durationField}>
            <label style={styles.label}>Nights</label>
            <input
              name="nights"
              type="number"
              min="0"
              placeholder="e.g., 6"
              value={form.nights}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.durationField}>
            <label style={styles.label}>Days</label>
            <input
              name="days"
              type="number"
              min="1"
              placeholder="e.g., 7"
              value={form.days}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
        </div>
        
        <textarea
          name="description"
          placeholder="Tour Description"
          value={form.description}
          onChange={handleChange}
          style={{ ...styles.input, height: '100px' }}
          required
        />
        
        {/* Image URL input (still available as fallback) */}
        <input
          name="image"
          placeholder="Image URL (or upload file below)"
          value={form.image}
          onChange={handleChange}
          style={styles.input}
          disabled={!!imageFile}
        />
        
        {/* File upload input */}
        <div style={styles.fileUploadContainer}>
          <label style={styles.fileUploadLabel}>
            {imageFile ? imageFile.name : 'Choose Image File'}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={styles.fileInput}
            />
          </label>
          {imageFile && (
            <button
              type="button"
              onClick={() => setImageFile(null)}
              style={styles.clearFileBtn}
            >
              ×
            </button>
          )}
        </div>
        
        {/* Progress indicator (optional) */}
        {isUploading && (
          <div style={styles.progressContainer}>
            <progress value={uploadProgress} max="100" style={styles.progressBar} />
            <span>{Math.round(uploadProgress)}%</span>
          </div>
        )}
        
        {/* Preview of selected image or existing image */}
        {(imageFile || form.image) && (
          <div style={styles.imagePreviewContainer}>
            <h4>Image Preview</h4>
            <img
              src={imageFile ? URL.createObjectURL(imageFile) : form.image}
              alt="Preview"
              style={styles.imagePreview}
            />
          </div>
        )}
        
        <button type="submit" style={styles.button} disabled={isUploading}>
          {isUploading ? 'Uploading...' : editId ? 'Update Tour' : 'Add Tour'}
        </button>
      </form>
      
      <hr style={{ margin: '40px 0' }} />
      
      <h3 style={{ marginBottom: '20px' }}>All International Tours</h3>
      <div style={styles.grid}>
        {tours.map(tour => (
          <div key={tour.id} style={styles.card}>
            <img src={tour.image} alt={tour.title} style={styles.cardImage} />
            <div style={styles.cardContent}>
              <h4>{tour.title}</h4>
              <p><strong>Duration:</strong> {tour.nights} Nights, {tour.days} Days</p>
              <p>{tour.description}</p>
              <div style={styles.btnGroup}>
                <button onClick={() => handleEdit(tour)} style={styles.editBtn}>Edit</button>
                <button onClick={() => handleDelete(tour.id)} style={styles.deleteBtn}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '40px auto',
    padding: '20px',
    fontFamily: '"Segoe UI", sans-serif'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    background: '#f0f8ff',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  input: {
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '16px'
  },
  durationContainer: {
    display: 'flex',
    gap: '15px'
  },
  durationField: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  button: {
    padding: '12px',
    backgroundColor: '#0077b6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column'
  },
  cardImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover'
  },
  cardContent: {
    padding: '15px'
  },
  btnGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '15px'
  },
  editBtn: {
    backgroundColor: '#007bff',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  deleteBtn: {
    backgroundColor: '#dc3545',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  fileUploadContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  fileUploadLabel: {
    padding: '10px 15px',
    backgroundColor: '#f0f0f0',
    borderRadius: '8px',
    border: '1px dashed #ccc',
    cursor: 'pointer',
    flex: '1',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  fileInput: {
    display: 'none'
  },
  clearFileBtn: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  progressBar: {
    flex: '1',
    height: '20px'
  },
  imagePreviewContainer: {
    marginTop: '10px'
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '8px',
    border: '1px solid #eee'
  }
};

export default AddInternationalTour;
